import { useRef, useState } from 'react';
import axios from '../../../utils/axios';
import { createCheckList, updateCheckList } from '../../../utils/urls';
import PreviewFile from './PreviewFile';

const resolveFileUrl = (url) => url?.startsWith('/') ? `https://media.bcrumbs.com${url}` : url;

export default function ChecklistModal({ visible, onHide, card, checklistHeadings, formId, userId, toast }) {
    const [localHeadings, setLocalHeadings] = useState(checklistHeadings || []);
    const [openItemId, setOpenItemId]     = useState(null);
    const [stagedFiles, setStagedFiles]   = useState({});
    const [saving, setSaving]             = useState(null);
    const [previewFile, setPreviewFile]   = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);

    // Add-item state
    const [addingToHeadingId, setAddingToHeadingId] = useState(null);
    const [newItemValue, setNewItemValue]            = useState('');
    const [addingLoading, setAddingLoading]          = useState(false);
    const [showHeadingPicker, setShowHeadingPicker]  = useState(false);

    const fileInputRefs = useRef({});

    const updateLocalItem = (itemId, patch) =>
        setLocalHeadings(prev => prev.map(h => ({
            ...h,
            items: (h.items || []).map(it =>
                it.checklist_id === itemId ? { ...it, ...patch } : it
            )
        })));

    const handleToggle = (item) => {
        const newVal = !item.completed;
        updateLocalItem(item.checklist_id, { completed: newVal });
        const fd = new FormData();
        fd.append('item', item.item || '');
        fd.append('due_date', item.due_date || '');
        fd.append('is_completed', newVal);
        fd.append('mention', JSON.stringify([]));
        fd.append('assigned_user', JSON.stringify([]));
        fd.append('removed_file_ids', JSON.stringify([]));
        fd.append('renamed_files', JSON.stringify([]));
        axios.post(`${updateCheckList}${item.checklist_id}/${userId}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).catch(() => {
            updateLocalItem(item.checklist_id, { completed: item.completed });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update', life: 3000 });
        });
    };

    const toggleDrawer = (itemId) =>
        setOpenItemId(prev => {
            if (prev === itemId) {
                setStagedFiles(p => { const n = { ...p }; delete n[itemId]; return n; });
                return null;
            }
            return itemId;
        });

    const stageFiles = (itemId, files) =>
        setStagedFiles(prev => ({
            ...prev,
            [itemId]: [...(prev[itemId] || []), ...files.map(f => ({ file: f, editedName: f.name }))]
        }));

    const unstageFile = (itemId, idx) =>
        setStagedFiles(prev => ({ ...prev, [itemId]: (prev[itemId] || []).filter((_, i) => i !== idx) }));

    const renameStagedFile = (itemId, idx, newName) =>
        setStagedFiles(prev => ({
            ...prev,
            [itemId]: (prev[itemId] || []).map((f, i) => i === idx ? { ...f, editedName: newName } : f)
        }));

    const handleDragOver  = e => { e.preventDefault(); e.currentTarget.classList.add('cklm-dz-drag'); };
    const handleDragLeave = e => e.currentTarget.classList.remove('cklm-dz-drag');
    const handleDrop      = (e, itemId) => {
        e.preventDefault();
        e.currentTarget.classList.remove('cklm-dz-drag');
        const files = [...e.dataTransfer.files];
        if (files.length) stageFiles(itemId, files);
    };

    const saveFiles = async (item) => {
        const newFiles = stagedFiles[item.checklist_id] || [];
        if (newFiles.length === 0) return;
        setSaving(item.checklist_id);
        try {
            const fd = new FormData();
            fd.append('item', item.item || '');
            fd.append('due_date', item.due_date || '');
            fd.append('is_completed', item.completed || false);
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('removed_file_ids', JSON.stringify([]));
            fd.append('renamed_files', JSON.stringify(
                (item.file_url || []).map(f => ({ id: f.id, file_name: f.file_name }))
            ));
            newFiles.forEach(f => {
                fd.append('file', f.file);
                fd.append('file_name', f.editedName);
            });
            const res = await axios.post(`${updateCheckList}${item.checklist_id}/${userId}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.status === 200) {
                updateLocalItem(item.checklist_id, { file_url: res.data.file_url || [] });
                setStagedFiles(p => { const n = { ...p }; delete n[item.checklist_id]; return n; });
                setOpenItemId(null);
                toast.current?.show({ severity: 'success', summary: 'Files saved', life: 2000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save files', life: 3000 });
        } finally {
            setSaving(null);
        }
    };

    // ── Add-item handlers ──────────────────────────────────────────────────────

    const handleAddClick = () => {
        if (localHeadings.length === 0) return;
        setNewItemValue('');
        if (localHeadings.length === 1) {
            setAddingToHeadingId(localHeadings[0].id);
            setShowHeadingPicker(false);
        } else {
            setShowHeadingPicker(prev => !prev);
            setAddingToHeadingId(null);
        }
    };

    const handlePickHeading = (headingId) => {
        setAddingToHeadingId(headingId);
        setShowHeadingPicker(false);
        setNewItemValue('');
    };

    const cancelAdd = () => {
        setAddingToHeadingId(null);
        setNewItemValue('');
        setShowHeadingPicker(false);
    };

    const handleSaveNewItem = async () => {
        if (!newItemValue.trim() || !addingToHeadingId) return;
        setAddingLoading(true);
        try {
            const fd = new FormData();
            fd.append('item', newItemValue.trim());
            fd.append('due_date', '');
            fd.append('heading_id', addingToHeadingId);
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            const res = await axios.post(`${createCheckList}${formId}/${userId}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.status === 200) {
                const newItem = {
                    checklist_id: res.data.checklist_id,
                    item: newItemValue.trim(),
                    completed: false,
                    file_url: [],
                    due_date: '',
                };
                setLocalHeadings(prev => prev.map(h =>
                    h.id === addingToHeadingId
                        ? { ...h, items: [...(h.items || []), newItem] }
                        : h
                ));
                setNewItemValue('');
                setAddingToHeadingId(null);
                toast.current?.show({ severity: 'success', summary: 'Item added', life: 2000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to add item', life: 3000 });
        } finally {
            setAddingLoading(false);
        }
    };

    // ──────────────────────────────────────────────────────────────────────────

    if (!visible) return null;

    const allItems = localHeadings.flatMap(h => h.items || []);
    const doneCount = allItems.filter(it => it.completed).length;

    return (
        <div className="cklm-backdrop" onClick={onHide}>
            <div className="cklm-modal" onClick={e => e.stopPropagation()}>

                {/* Close */}
                <button className="cklm-x" onClick={onHide} title="Close">✕</button>

                {/* Header */}
                <div className="cklm-hd">
                    <div className="cklm-hd-left">
                        <div className="cklm-name">{card?.full_name || 'Checklist'}</div>
                        <div className="cklm-sub">
                            Next Steps &amp; Documents
                            <span className="cklm-progress-txt">{doneCount}/{allItems.length} done</span>
                        </div>
                    </div>
                    <div className="cklm-progress-bar">
                        {allItems.map((it, i) => (
                            <span key={i} className={`cklm-step${it.completed ? ' done' : ''}`} />
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="cklm-body">
                    {localHeadings.length === 0 && (
                        <p className="cklm-empty">No checklist items.</p>
                    )}

                    {localHeadings.map(heading => (
                        <div key={heading.id} className="cklm-section">
                            <div className="cklm-section-hd">{heading.name}</div>

                            {(heading.items || []).length === 0 && addingToHeadingId !== heading.id && (
                                <p className="cklm-empty-section">No items in this heading.</p>
                            )}

                            {(heading.items || []).map(item => {
                                const isOpen       = openItemId === item.checklist_id;
                                const staged       = stagedFiles[item.checklist_id] || [];
                                const isSavingThis = saving === item.checklist_id;
                                const fileCount    = (item.file_url || []).length;

                                return (
                                    <div key={item.checklist_id} className={`cklm-item${item.completed ? ' done' : ''}${isOpen ? ' open' : ''}`}>

                                        {/* Row */}
                                        <div className="cklm-item-row" onClick={() => toggleDrawer(item.checklist_id)}>
                                            <div
                                                className={`cklm-cb${item.completed ? ' checked' : ''}`}
                                                onClick={e => { e.stopPropagation(); handleToggle(item); }}
                                                title="Toggle completion"
                                            >
                                                {item.completed && '✓'}
                                            </div>
                                            <span className="cklm-item-name">{item.item || '(unnamed)'}</span>
                                            <div className="cklm-item-meta">
                                                {fileCount > 0 && (
                                                    <span className="cklm-attach-pill">📎 {fileCount}</span>
                                                )}
                                                <span className="cklm-chevron">{isOpen ? '▲' : '▼'}</span>
                                            </div>
                                        </div>

                                        {/* File chips — collapsed view */}
                                        {!isOpen && fileCount > 0 && (
                                            <div className="cklm-chips">
                                                {(item.file_url || []).map((f, i) => (
                                                    <button key={i} className="cklm-chip"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) });
                                                            setPreviewVisible(true);
                                                        }}>
                                                        📄 <span>{f.file_name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Upload drawer — open */}
                                        {isOpen && (
                                            <div className="cklm-drawer">
                                                <input
                                                    type="file"
                                                    multiple
                                                    ref={el => { fileInputRefs.current[item.checklist_id] = el; }}
                                                    style={{ display: 'none' }}
                                                    onChange={e => { stageFiles(item.checklist_id, [...e.target.files]); e.target.value = ''; }}
                                                />

                                                <div
                                                    className="cklm-dropzone"
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={e => handleDrop(e, item.checklist_id)}
                                                    onClick={() => fileInputRefs.current[item.checklist_id]?.click()}
                                                >
                                                    ⬆ <b>Drag &amp; drop</b> or click to upload
                                                </div>

                                                {/* Existing files */}
                                                {fileCount > 0 && (
                                                    <div className="cklm-file-list">
                                                        {(item.file_url || []).map((f, i) => (
                                                            <div key={i} className="cklm-file-row">
                                                                <span className="cklm-file-name"
                                                                    onClick={() => {
                                                                        setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) });
                                                                        setPreviewVisible(true);
                                                                    }}>
                                                                    📄 {f.file_name}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Staged new files */}
                                                {staged.length > 0 && (
                                                    <div className="cklm-file-list cklm-staged-list">
                                                        {staged.map((f, i) => (
                                                            <div key={i} className="cklm-file-row cklm-file-new">
                                                                <span className="cklm-file-ic">📄</span>
                                                                <input
                                                                    className="cklm-file-name-input"
                                                                    value={f.editedName}
                                                                    onChange={e => renameStagedFile(item.checklist_id, i, e.target.value)}
                                                                    onClick={e => e.stopPropagation()}
                                                                />
                                                                <button className="cklm-file-rm" onClick={() => unstageFile(item.checklist_id, i)}>✕</button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Drawer actions */}
                                                <div className="cklm-drawer-actions">
                                                    {staged.length > 0 && (
                                                        <button className="cklm-btn-save" onClick={() => saveFiles(item)} disabled={isSavingThis}>
                                                            {isSavingThis ? 'Saving…' : 'Save files'}
                                                        </button>
                                                    )}
                                                    <button
                                                        className={`cklm-btn-mark${item.completed ? ' incomplete' : ''}`}
                                                        onClick={e => { e.stopPropagation(); handleToggle(item); }}
                                                    >
                                                        {item.completed ? 'Mark incomplete' : '✓ Mark complete'}
                                                    </button>
                                                    <button className="cklm-btn-cancel" onClick={() => toggleDrawer(item.checklist_id)}>Close</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Inline add-item form for this heading */}
                            {addingToHeadingId === heading.id && (
                                <div className="cklm-add-form">
                                    <input
                                        className="cklm-add-input"
                                        autoFocus
                                        placeholder="Item name…"
                                        value={newItemValue}
                                        onChange={e => setNewItemValue(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleSaveNewItem();
                                            if (e.key === 'Escape') cancelAdd();
                                        }}
                                    />
                                    <div className="cklm-add-form-actions">
                                        <button
                                            className="cklm-btn-save"
                                            onClick={handleSaveNewItem}
                                            disabled={!newItemValue.trim() || addingLoading}
                                        >
                                            {addingLoading ? 'Adding…' : 'Add item'}
                                        </button>
                                        <button className="cklm-btn-cancel" onClick={cancelAdd}>Cancel</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="cklm-footer">
                    <div className="cklm-footer-left">
                        <button className="cklm-btn-add-item" onClick={handleAddClick}>
                            + Add item
                        </button>

                        {/* Heading picker — shown when multiple headings */}
                        {showHeadingPicker && (
                            <div className="cklm-heading-picker">
                                <div className="cklm-heading-picker-label">Select a section:</div>
                                {localHeadings.map(h => (
                                    <button
                                        key={h.id}
                                        className="cklm-heading-option"
                                        onClick={() => handlePickHeading(h.id)}
                                    >
                                        {h.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className="cklm-btn-close-modal" onClick={onHide}>Close</button>
                </div>
            </div>

            {/* File preview overlay */}
            {previewVisible && previewFile && (
                <div className="cklm-preview-backdrop" onClick={() => { setPreviewVisible(false); setPreviewFile(null); }}>
                    <div className="cklm-preview-box" onClick={e => e.stopPropagation()}>
                        <button className="cklm-preview-x" onClick={() => { setPreviewVisible(false); setPreviewFile(null); }}>✕</button>
                        <PreviewFile isSingleFile={true} setPreviewVisible={setPreviewVisible} previewFile={previewFile} />
                    </div>
                </div>
            )}
        </div>
    );
}
